import { Component } from 'react';
import { Calendar as CalendarComponent, Modifiers } from '../Calendar';
import { noop } from '../utils/func';
import { CalendarService } from '../utils/services';

export interface DatePickerProps<D, M, Y> {
    date?: D;
    getCalendarService: () => CalendarService<D, M, Y>;
    onDateClick: (event: React.MouseEvent, date: D) => void;
    onDateChange: (date: D) => void;
    getDateTooltip?: (date: D) => React.ReactNode | undefined;
    allowDisabledClick?: boolean;
    month?: M;
    dataTestName?: string;
    minDate?: D;
    maxDate?: D;
    modifiers?: Modifiers<D>;
    numberOfMonths?: number;
    enableAdjacentMonthSelection?: boolean;
}

interface DatePickerState<D> {
    selectedDate: D;
}

class DatePickerElement<D, M, Y> extends Component<DatePickerProps<D, M, Y>, DatePickerState<D>> {
    private modifiers?: Modifiers<D>;

    static defaultProps = {
        onDateClick: noop,
        onDateChange: noop,
    };

    constructor(props: DatePickerProps<D, M, Y>) {
        super(props);

        const { date, minDate, getCalendarService } = props;
        const currentDate = getCalendarService().getCurrentDate();
        const defaultDate =
            minDate != null && getCalendarService().isDateGreater(minDate, currentDate) ? minDate : currentDate;

        this.state = {
            selectedDate: date || defaultDate,
        };

        this.modifiers = {
            disabled: this.isDateDisabled,
            selected: this.isSelected,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: DatePickerProps<D, M, Y>) {
        if (nextProps.date && !this.props.getCalendarService().isDateEqual(nextProps.date, this.state.selectedDate)) {
            this.setState({
                selectedDate: nextProps.date,
            });
        }
    }

    handleDateClick = (e: React.MouseEvent, date: D) => {
        const isDisabled = this.props.modifiers?.disabled?.(date) ?? false;
        if (this.props.allowDisabledClick && isDisabled) {
            // user clicked to date which is not selectable
            this.props.onDateClick(e, date);
            return;
        }
        this.setState({ selectedDate: date });
        this.props.onDateChange(date);
        this.props.onDateClick(e, date);
    };

    isDateDisabled = (date: D) =>
        this.props
            .getCalendarService()
            .isDateDisabled(date, { minDate: this.props.minDate, maxDate: this.props.maxDate });

    isSelected = (date: D) =>
        this.props.getCalendarService().isDateEqual(date, this.state.selectedDate) &&
        !this.props.modifiers?.disabled?.(date);

    render() {
        const { getCalendarService, modifiers, onDateChange, month, ...other } = this.props;
        const { selectedDate } = this.state;

        const calendarProps = {
            ...other,
            getCalendarService,
            month: month ?? getCalendarService().getMonthOfDate(selectedDate),
            modifiers: {
                ...this.modifiers,
                ...modifiers,
            },
            onDateClick: this.handleDateClick,
            singlePicker: true,
            numberOfMonths: this.props.numberOfMonths ? this.props.numberOfMonths : 1,
        };

        return <CalendarComponent {...calendarProps} />;
    }
}

export default DatePickerElement;
